import React, { forwardRef, useContext, useRef } from 'react';
import { useMutation } from '@apollo/client';
import SaveOrderLineProductTempMutation from '../../../graphql/mutations/sales/SaveOrderLineProductTempMutation.graphql';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { default as Form } from '../../../components/connectedForm';
import getClientMutationId from '../../../utils/getClientMutationId';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { IEditableDataGridProps } from '../../../components/EditableDataGrid/EditableDataGrid';
import {
  ISalesOrder,
  ISalesOrderLineInput,
  ISalesOrderLineTemporaryMutationResult,
  ISalesOrderLineTemporaryMutationVariables,
} from '../../../graphql/schema/p4types';
import { FieldProps } from '../../../components/connectedForm/Field';

/**
 * A wrapper for the Sales Order "Items" grid for adding per-line temporary save capability per P4-2333
 * It needs to be at this level because the additional prop it adds (customChange) is consumed at the ConnectedField level.
 * @param gridProps - All props passed to this element are passed through to ConnectedField/EditableDataGrid.
 * @returns {JSX.Element} - A connected field, which expects an EditableDataGrid (Or extension) as it's component.
 */
const SalesOrderItemGrid = forwardRef<any, FieldProps<IEditableDataGridProps>>((gridProps, ref) => {
  const [temporarySaveSalesOrderLine] = useMutation<
    ISalesOrderLineTemporaryMutationResult,
    ISalesOrderLineTemporaryMutationVariables
  >(SaveOrderLineProductTempMutation);
  const connectedForm = useContext(ConnectedFormContext);

  const salesOrder = connectedForm?.values || ({} as Partial<ISalesOrder>);

  const minimalHeader = {
    id: salesOrder.id,
    uuid: salesOrder.uuid,
    concurrencyVersion: salesOrder.concurrencyVersion,
    customer: salesOrder.customer,
    currency: salesOrder.currency,
    foreignCurrency: salesOrder.foreignCurrency,
    exchangeRate: salesOrder.exchangeRate,
    vatBusinessCategory: salesOrder.vatBusinessCategory,
    vatInclusive: salesOrder.vatInclusive,
    defaultPriceBand: salesOrder.defaultPriceBand,
  };

  const lastMutationId = useRef<string | undefined>();

  return (
    <Form.Field<IEditableDataGridProps<ISalesOrderLineInput> & React.RefAttributes<any>>
      ref={ref}
      {...gridProps}
      customChange={({ commit, updateStore, dataid, column }) => {
        //If the quantity is >0 (Either before or after this commit), we may need to just trigger a regular temporary
        // save to recalculate line values (Only if the cellKey was the product - No need for a temporary save on the other fields).
        if (commit.cellKey !== 'product') {
          return { temporary: !!column?.temporaryOnChange };
        } else if (commit.original?.quantity > 0 || commit.updated?.quantity > 0) {
          return { temporary: true };
        }

        const value = commit.original || {};
        commit.cellKeys.forEach((key) => {
          value[key as keyof ISalesOrderLineInput] = commit.updated?.[key as keyof ISalesOrderLineInput];
        });

        //Only trigger the line-save if product is defined.
        if (value.product) {
          value.salesOrder = minimalHeader;

          const mutationId = getClientMutationId();
          lastMutationId.current = mutationId;

          temporarySaveSalesOrderLine({
            variables: {
              input: {
                clientMutationId: mutationId,
                salesOrderLine: normalizeVariables(value),
              },
            },
          }).then((result) => {
            if (mutationId !== lastMutationId.current) {
              return;
            }

            //Update the store for this particular row with the updated details from the services.
            const singleRowResult = result.data?.salesOrderLineTemporary?.result as ISalesOrderLineInput;

            updateStore(connectedForm.change, false, `${dataid}.${commit.rowIdx}`, singleRowResult);
          });
        }

        //This lets ConnectedField.js know not to fire a temporary save off its' initial store update (That sets the Product, Description & Warehouse).
        return {
          temporary: false,
        };
      }}
    />
  );
});

//This is (currently) a required hack because other code checks the displayName and acts differently based on it.
(SalesOrderItemGrid as any).displayName = 'EditableDataGrid';

export default SalesOrderItemGrid;
